import React, { useId, useMemo } from "react";

/**
 * Lightweight inline SVG sparkline. No deps — picks up the highest of `cost`
 * or `calls` and normalizes both to 0-1 of the SVG viewBox height.
 */
export default function Sparkline({
    points = [],
    metric = "cost", // 'cost' | 'calls' | 'tokens'
    color = "#7C3AED",
    height = 36,
    width = 200,
    showAxis = true,
    title,
}) {
    const gradientId = `spark-${useId().replace(/:/g, "")}`;
    const series = useMemo(() => {
        const valKey = metric === "cost" ? "cost_usd" : metric === "tokens" ? "tokens_total" : "calls";
        return (points || []).map((p) => ({
            bucket: p.bucket,
            value: Number(p[valKey] || 0),
        }));
    }, [points, metric]);

    const max = useMemo(() => series.reduce((m, p) => Math.max(m, p.value), 0), [series]);
    const total = useMemo(() => series.reduce((s, p) => s + p.value, 0), [series]);
    const last = series.length > 0 ? series[series.length - 1].value : 0;

    if (series.length === 0) {
        return (
            <div className="flex items-center justify-center text-[10px] text-white/30 italic" style={{ height }}>
                no activity
            </div>
        );
    }

    const stepX = series.length > 1 ? width / (series.length - 1) : width;
    const path = series
        .map((p, i) => {
            const x = i * stepX;
            const y = max === 0 ? height : height - (p.value / max) * height;
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    const fillPath = `${path} L${width},${height} L0,${height} Z`;

    return (
        <div title={title}>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                height={height}
                preserveAspectRatio="none"
                role="img"
                aria-label={`${formatVal(metric, total)} total over ${series.length} ${series.length === 1 ? "bucket" : "buckets"}`}
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.32" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                <path d={fillPath} fill={`url(#${gradientId})`} />
                <path d={path} fill="none" stroke={color} strokeWidth="1.25" />
                {/* highlight last point */}
                {(() => {
                    const i = series.length - 1;
                    const x = i * stepX;
                    const y = max === 0 ? height : height - (series[i].value / max) * height;
                    return <circle cx={x} cy={y} r="2" fill={color} />;
                })()}
            </svg>
            {showAxis && (
                <div className="flex items-center justify-between text-[9px] text-white/30 font-mono mt-1">
                    <span>{formatVal(metric, total)} total</span>
                    <span>last: {formatVal(metric, last)}</span>
                </div>
            )}
        </div>
    );
}

function formatVal(metric, v) {
    if (metric === "cost") return `$${Number(v || 0).toFixed(5)}`;
    if (metric === "tokens") {
        const n = Number(v || 0);
        if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
        return `${n}`;
    }
    return Number(v || 0).toLocaleString();
}
